import React from 'react';
import { toast } from 'react-toastify';
import FranklyCloudModal, { ImageLightbox } from '../FranklyCloudModal';

const MAX_IMAGES = 10;

const refToItem = (ref, cloudItemsMap) => {
    if (!ref) return null;
    const resolved = cloudItemsMap?.[ref.id];
    if (resolved && !resolved.deleted) return { ...ref, ...resolved };
    if (ref.type === 'upload' && ref.src) return ref;
    return null;
};

const GalleryBox = ({
    gallery,
    galleryId,
    isEditing,
    isOwnProfile,
    bare = false,
    onUpdate,
    cloudItemsMap,
    cloudItemsMapReady,
    userData,
    cloudUploadMode,
}) => {
    const images = Array.isArray(gallery?.images) ? gallery.images : [];
    const imageMode = gallery?.imageMode === 'native' ? 'native' : 'stretched';
    const showHeader = gallery?.showHeader !== false;

    const [activeIndex, setActiveIndex] = React.useState(0);
    const [lightboxIndex, setLightboxIndex] = React.useState(null);
    const [cloudModalOpen, setCloudModalOpen] = React.useState(false);

    React.useEffect(() => {
        if (activeIndex >= images.length) setActiveIndex(Math.max(0, images.length - 1));
    }, [images.length, activeIndex]);

    const resolvedItems = React.useMemo(
        () => images.map(ref => refToItem(ref, cloudItemsMap)),
        [images, cloudItemsMap]
    );

    const lightboxItems = React.useMemo(
        () => resolvedItems
            .map((it, i) => it ? { src: it.src, name: it.name || '', _origIndex: i } : null)
            .filter(Boolean),
        [resolvedItems]
    );

    const handlePrev = (e) => {
        e?.stopPropagation?.();
        setActiveIndex(i => (i - 1 + images.length) % images.length);
    };
    const handleNext = (e) => {
        e?.stopPropagation?.();
        setActiveIndex(i => (i + 1) % images.length);
    };

    const handleCloudInsert = (item) => {
        if (!isEditing || !isOwnProfile) return;
        if (images.length >= MAX_IMAGES) {
            toast.warning(`Gallery is full (${MAX_IMAGES} images max).`);
            return;
        }
        const ref = {
            id: item.id,
            type: item.type,
            name: item.name || '',
            ...(item.type === 'upload' && item.src ? { src: item.src } : {}),
        };
        if (images.some(img => String(img.id) === String(item.id))) {
            toast.info('That image is already in this gallery.');
            setCloudModalOpen(false);
            return;
        }
        onUpdate?.({ images: [...images, ref] });
        setCloudModalOpen(false);
    };

    const handleRemoveImage = (idx) => {
        const next = images.filter((_, i) => i !== idx);
        onUpdate?.({ images: next });
        if (activeIndex >= next.length) setActiveIndex(Math.max(0, next.length - 1));
    };

    const handleMoveImage = (idx, dir) => {
        const targetIdx = idx + dir;
        if (targetIdx < 0 || targetIdx >= images.length) return;
        const next = [...images];
        [next[idx], next[targetIdx]] = [next[targetIdx], next[idx]];
        onUpdate?.({ images: next });
    };

    const stopProps = (e) => e.stopPropagation();

    const currentItem = resolvedItems[activeIndex] || null;
    const currentRef = images[activeIndex] || null;
    const showCycleArrows = images.length > 1;

    const renderImageArea = () => (
        <div
            className={`relative w-full flex items-center justify-center ${bare ? '' : 'bg-black/5'} overflow-hidden`}
            style={{ minHeight: images.length === 0 ? 120 : undefined }}
        >
            {images.length === 0 ? (
                <div className="text-coal/60 text-sm italic py-8 px-3 text-center">
                    {isEditing && isOwnProfile
                        ? 'No images yet. Click "Add Image" to insert one from your FranklyCloud.'
                        : 'This gallery is empty.'}
                </div>
            ) : !cloudItemsMapReady && !currentItem ? (
                <div className="text-coal/60 text-sm py-8 px-3 animate-pulse">Loading…</div>
            ) : !currentItem ? (
                <div className="text-coal/60 text-sm py-8 px-3 italic text-center">
                    Image unavailable{currentRef?.name ? ` (${currentRef.name})` : ''}.
                </div>
            ) : (
                <img
                    src={currentItem.src}
                    alt={currentItem.name || ''}
                    onClick={() => setLightboxIndex(lightboxItems.findIndex(it => it._origIndex === activeIndex))}
                    draggable={false}
                    className="block cursor-pointer"
                    style={imageMode === 'stretched'
                        ? { userSelect: 'none', width: '100%', maxWidth: '100%', maxHeight: '700px', height: 'auto', objectFit: 'contain' }
                        : { userSelect: 'none', maxWidth: '100%', maxHeight: '700px', width: 'auto', height: 'auto' }
                    }
                />
            )}

            {showCycleArrows && images.length > 0 && currentItem && (
                <>
                    <button
                        type="button"
                        onClick={handlePrev}
                        onPointerDown={stopProps}
                        onMouseDown={stopProps}
                        className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center bg-black/45 hover:bg-black/65 text-white text-2xl leading-none rounded-full select-none z-10"
                        title="Previous"
                    >
                        ‹
                    </button>
                    <button
                        type="button"
                        onClick={handleNext}
                        onPointerDown={stopProps}
                        onMouseDown={stopProps}
                        className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center bg-black/45 hover:bg-black/65 text-white text-2xl leading-none rounded-full select-none z-10"
                        title="Next"
                    >
                        ›
                    </button>
                    <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1.5 z-10 pointer-events-none">
                        {images.map((_, i) => (
                            <div key={i} className={`w-1.5 h-1.5 rounded-full ${i === activeIndex ? 'bg-white' : 'bg-white/40'}`} />
                        ))}
                    </div>
                </>
            )}
        </div>
    );

    const renderEditToolbar = () => (
        <div className="flex flex-wrap items-center gap-1.5 px-2 py-2 border-b border-black/20 bg-black/5">
            <button
                type="button"
                onClick={(e) => { stopProps(e); onUpdate?.({ showHeader: !showHeader }); }}
                onPointerDown={stopProps}
                onMouseDown={stopProps}
                title="Toggle whether the header shows when viewing this profile"
                className={`px-2 py-1 text-[9px] uppercase font-bold border border-black/30 rounded whitespace-nowrap transition-colors ${
                    showHeader ? 'bg-gold text-white border-gold' : 'bg-white text-coal/50'
                }`}
            >
                {showHeader ? 'Header/Skin: Shown' : 'Header/Skin: Hidden'}
            </button>
            <button
                type="button"
                onClick={(e) => { stopProps(e); onUpdate?.({ imageMode: imageMode === 'native' ? 'stretched' : 'native' }); }}
                onPointerDown={stopProps}
                onMouseDown={stopProps}
                title="Toggle whether images stretch to fit the box width or display at native size"
                className={`px-2 py-1 text-[9px] uppercase font-bold border border-black/30 rounded whitespace-nowrap transition-colors ${
                    imageMode === 'stretched' ? 'bg-gold text-white border-gold' : 'bg-white text-coal/50'
                }`}
            >
                {imageMode === 'stretched' ? 'Fit to Box' : '100%'}
            </button>
            <div className="flex-1 min-w-0" />
            <span className="text-[10px] text-coal/50 whitespace-nowrap">{images.length}/{MAX_IMAGES}</span>
            <button
                type="button"
                onClick={(e) => { stopProps(e); setCloudModalOpen(true); }}
                onPointerDown={stopProps}
                onMouseDown={stopProps}
                disabled={images.length >= MAX_IMAGES}
                className="px-2 py-1 text-[9px] uppercase font-bold border border-black/30 rounded bg-white text-coal hover:bg-black/10 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
            >
                + Add Image
            </button>
        </div>
    );

    const renderImageChips = () => (
        images.length === 0 ? null : (
            <div className="flex flex-row flex-wrap gap-1 p-2 border-t border-black/15 bg-black/5">
                {images.map((ref, i) => {
                    const res = resolvedItems[i];
                    const isActive = i === activeIndex;
                    return (
                        <div
                            key={`${ref.id}-${i}`}
                            className={`relative group w-14 h-14 rounded overflow-hidden border ${isActive ? 'border-gold' : 'border-black/20'} cursor-pointer`}
                            onClick={(e) => { stopProps(e); setActiveIndex(i); }}
                            onPointerDown={stopProps}
                            onMouseDown={stopProps}
                            title={ref.name || ''}
                        >
                            {res?.src ? (
                                <img src={res.src} alt={ref.name || ''} className="w-full h-full object-cover" draggable={false} />
                            ) : (
                                <div className="w-full h-full bg-black/10 flex items-center justify-center text-[9px] text-coal/50">N/A</div>
                            )}
                            <div className="absolute inset-x-0 bottom-0 flex justify-between opacity-0 group-hover:opacity-100 transition-opacity bg-black/70">
                                <button
                                    type="button"
                                    onClick={(e) => { stopProps(e); handleMoveImage(i, -1); }}
                                    disabled={i === 0}
                                    className="flex-1 text-white text-[10px] leading-none py-0.5 hover:bg-white/20 disabled:opacity-30"
                                    title="Move left"
                                >‹</button>
                                <button
                                    type="button"
                                    onClick={(e) => { stopProps(e); handleRemoveImage(i); }}
                                    className="flex-1 text-white text-[10px] leading-none py-0.5 hover:bg-red-700"
                                    title="Remove"
                                >✕</button>
                                <button
                                    type="button"
                                    onClick={(e) => { stopProps(e); handleMoveImage(i, 1); }}
                                    disabled={i === images.length - 1}
                                    className="flex-1 text-white text-[10px] leading-none py-0.5 hover:bg-white/20 disabled:opacity-30"
                                    title="Move right"
                                >›</button>
                            </div>
                        </div>
                    );
                })}
            </div>
        )
    );

    return (
        <div className="w-full flex flex-col">
            {isEditing && isOwnProfile && renderEditToolbar()}
            {renderImageArea()}
            {isEditing && isOwnProfile && renderImageChips()}

            {lightboxIndex !== null && lightboxItems[lightboxIndex] && (
                <ImageLightbox
                    items={lightboxItems}
                    index={lightboxIndex}
                    onChange={(i) => {
                        setLightboxIndex(i);
                        const orig = lightboxItems[i]?._origIndex;
                        if (typeof orig === 'number') setActiveIndex(orig);
                    }}
                    onClose={() => setLightboxIndex(null)}
                />
            )}

            {cloudModalOpen && (
                <FranklyCloudModal
                    isOpen={cloudModalOpen}
                    onClose={() => setCloudModalOpen(false)}
                    userData={userData}
                    cloudUploadMode={cloudUploadMode || 'all'}
                    onInsert={handleCloudInsert}
                />
            )}
        </div>
    );
};

export default GalleryBox;
